// Raw AAC in ADTS frames (.aac files).
//
// ADTS ("Audio Data Transport Stream") wraps each AAC frame in a 7-byte header
// (9 with a CRC) that starts with a 12-bit sync word 0xFFF. It looks a lot like
// MPEG audio, but the two "layer" bits are always 00, which MPEG audio never uses.
import { BitFields, channelName, fmtBytes, fmtHz, fmtKbps, fmtSec, hex, matches, plural } from './bits';
import { id3Explanations, parseId3v2 } from './id3';
import { parseTrailingTags, trailingTagExplanations } from './mp3';
import type { Explanation, Field, FormatParser, Note, ParsedFile, Picture, Segment, Tag } from './types';

const SAMPLE_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];
const PROFILES = ['Main', 'LC', 'SSR', 'LTP'];
const CODECS = ['AAC Main', 'AAC-LC', 'AAC SSR', 'AAC LTP'];
const SAMPLES_PER_BLOCK = 1024;

// Byte-level accessors for the fixed + variable header.
const isSync = (b: Uint8Array, p: number) => b[p] === 0xff && (b[p + 1] & 0xf6) === 0xf0;
const protAbsent = (b: Uint8Array, p: number) => b[p + 1] & 1;
const profileOf = (b: Uint8Array, p: number) => b[p + 2] >> 6;
const sfiOf = (b: Uint8Array, p: number) => (b[p + 2] >> 2) & 15;
const chanOf = (b: Uint8Array, p: number) => ((b[p + 2] & 1) << 2) | (b[p + 3] >> 6);
const frameLenOf = (b: Uint8Array, p: number) => ((b[p + 3] & 3) << 11) | (b[p + 4] << 3) | (b[p + 5] >> 5);
const fullnessOf = (b: Uint8Array, p: number) => ((b[p + 5] & 0x1f) << 6) | (b[p + 6] >> 2);
const blocksOf = (b: Uint8Array, p: number) => (b[p + 6] & 3) + 1;
const headerLen = (b: Uint8Array, p: number) => (protAbsent(b, p) ? 7 : 9);
/** The header bits that stay fixed for a stream: MPEG ID, profile, sample rate, channels. */
const streamKey = (b: Uint8Array, p: number) => ((b[p + 1] & 0xf8) << 16) | ((b[p + 2] & 0xfd) << 8) | (b[p + 3] & 0xc0);

function validAt(b: Uint8Array, p: number, end: number): boolean {
  return p + 7 <= end && isSync(b, p) && sfiOf(b, p) < 13 && frameLenOf(b, p) >= headerLen(b, p);
}

/** True when a frame at p is followed by `need` more frames of the same stream (or the end). */
function chainAt(b: Uint8Array, p: number, end: number, need: number, key: number | null): boolean {
  if (!validAt(b, p, end)) return false;
  const k = streamKey(b, p);
  if (key !== null && k !== key) return false;
  for (let i = 0; i < need; i++) {
    const q = p + frameLenOf(b, p);
    if (q === end || (q > end && i > 0)) return true;
    if (!validAt(b, q, end) || streamKey(b, q) !== k) return false;
    p = q;
  }
  return true;
}

function findSync(b: Uint8Array, from: number, end: number, key: number | null, need: number): number {
  for (let i = from; i + 7 <= end; i++) if (b[i] === 0xff && chainAt(b, i, end, need, key)) return i;
  return end;
}

function skipId3(b: Uint8Array, p: number): number {
  for (let guard = 0; guard < 16 && matches(b, p, 'ID3') && !((b[p + 6] | b[p + 7] | b[p + 8] | b[p + 9]) & 0x80); guard++) {
    const footer = b[p + 3] === 4 && b[p + 5] & 0x10 ? 10 : 0;
    p += 10 + (((b[p + 6] & 0x7f) << 21) | ((b[p + 7] & 0x7f) << 14) | ((b[p + 8] & 0x7f) << 7) | (b[p + 9] & 0x7f)) + footer;
  }
  return p;
}

function headerFields(b: Uint8Array, p: number): Field[] {
  const bf = new BitFields(b, p);
  bf.read('Sync word', 12, (v) => hex(v, 3), 'Twelve 1-bits that mark the start of each frame.');
  bf.read('MPEG version', 1, (v) => (v ? 'MPEG-2' : 'MPEG-4'), 'Which standard the stream follows. For plain AAC-LC the audio is the same.');
  bf.read('Layer', 2, undefined, 'Always 0. This is how ADTS differs from an MP3 frame header.');
  const pa = bf.read('Protection absent', 1, (v) => (v ? 'yes (no CRC)' : 'no (CRC follows)'), 'A 0 here means a 16-bit CRC follows the header.');
  bf.read('Profile', 2, (v) => `${PROFILES[v]} (object type ${v + 1})`, 'The AAC variant. LC (Low Complexity) is by far the most common.');
  bf.read('Sampling frequency index', 4, (v) => (SAMPLE_RATES[v] ? fmtHz(SAMPLE_RATES[v]) : 'reserved'), 'Picks the sample rate from a table of 13 rates.');
  bf.read('Private bit', 1, undefined, 'Free for any use. Decoders ignore it.');
  bf.read('Channel configuration', 3, (v) => (v === 0 ? 'set in stream (PCE)' : channelName(v === 7 ? 8 : v)), 'How many speakers, and which ones: 1 is mono, 2 is stereo, 6 is 5.1.');
  bf.read('Original/copy', 1, undefined, 'A flag that says whether this is the original. Nothing enforces it.');
  bf.read('Home', 1, undefined, 'Unused; always 0 in practice.');
  bf.read('Copyright ID bit', 1, undefined, 'One bit of a copyright ID, sent across many frames.');
  bf.read('Copyright ID start', 1, undefined, 'Marks where the copyright ID bits start.');
  bf.read('Frame length', 13, (v) => `${v} bytes`, 'Length of the whole frame, header included. This is how a reader finds the next frame.');
  bf.read('Buffer fullness', 11, (v) => (v === 0x7ff ? '0x7FF (variable bitrate)' : String(v)), 'How full the decoder\'s bit buffer is. 0x7FF means variable bitrate.');
  bf.read('Raw data blocks', 2, (v) => `${v + 1}`, 'Number of AAC frames in this ADTS frame, minus one. Almost always 0 (one frame).');
  if (!pa) bf.read('CRC', 16, (v) => hex(v, 4), 'A checksum that lets a decoder spot damaged frames.');
  return bf.fields;
}

class AdtsFrame implements Segment {
  readonly kind = 'audio' as const;
  readonly type = 'aac.frame';
  declare warning?: string;
  constructor(
    readonly b: Uint8Array,
    readonly start: number,
    readonly end: number,
    readonly time: [number, number],
    readonly index: number,
  ) {}
  get name(): string {
    return `Frame ${(this.index + 1).toLocaleString('en-US')}`;
  }
  get summary(): string {
    const { b, start } = this;
    const sr = SAMPLE_RATES[sfiOf(b, start)];
    const kbps = ((this.end - this.start) * 8 * sr) / (SAMPLES_PER_BLOCK * blocksOf(b, start)) / 1000;
    const ch = chanOf(b, start);
    return `${Math.round(kbps)} kbps · ${fmtHz(sr)} · ${ch ? channelName(ch === 7 ? 8 : ch) : 'PCE'}`;
  }
  get fields(): () => Field[] {
    return () => (this.end - this.start >= headerLen(this.b, this.start) ? headerFields(this.b, this.start) : []);
  }
  get children(): () => Segment[] {
    return () => {
      const { b, start, end } = this;
      const hl = Math.min(end - start, headerLen(b, start));
      const out: Segment[] = [{ kind: 'header', type: 'aac.header', name: 'ADTS header', start, end: start + hl, fields: hl === headerLen(b, start) ? headerFields(b, start) : [], summary: `${hl} bytes` }];
      const n = blocksOf(b, start);
      if (start + hl < end)
        out.push({ kind: 'audio', type: 'aac.rawdata', name: n > 1 ? 'Raw data blocks' : 'Raw data block', start: start + hl, end, summary: `${fmtBytes(end - start - hl)} · ${plural(n * SAMPLES_PER_BLOCK, 'sample')}` });
      return out;
    };
  }
}

function detect(b: Uint8Array): boolean {
  const p = skipId3(b, 0);
  if (validAt(b, p, b.length) && chainAt(b, p, b.length, 2, null)) return true;
  const lim = Math.min(b.length, p + 8192);
  for (let i = p + 1; i + 7 <= lim; i++) if (b[i] === 0xff && chainAt(b, i, b.length, 4, null)) return true;
  return false;
}

function parse(b: Uint8Array): ParsedFile {
  const size = b.length;
  const top: Segment[] = [];
  const tags: Tag[] = [];
  const pictures: Picture[] = [];
  const notes: Note[] = [];
  let tagBytes = 0;

  let pos = 0;
  for (let guard = 0; guard < 16; guard++) {
    const r = parseId3v2(b, pos);
    if (!r || r.end <= pos) break;
    top.push(r.segment);
    tags.push(...r.tags);
    pictures.push(...r.pictures);
    notes.push(...r.notes);
    tagBytes += r.end - pos;
    pos = r.end;
  }
  const tail = parseTrailingTags(b, pos);
  const end = tail.start;
  tags.push(...tail.tags);
  pictures.push(...tail.pictures);
  for (const s of tail.segments) tagBytes += s.end - s.start;

  let first = chainAt(b, pos, end, 2, null) ? pos : findSync(b, pos, end, null, 3);
  if (first >= end) first = findSync(b, pos, end, null, 1);
  if (first > pos) {
    top.push({ kind: 'unknown', type: 'aac.junk', name: 'Junk before audio', start: pos, end: first, summary: fmtBytes(first - pos), warning: first < end ? 'These bytes come before the first ADTS frame.' : 'No ADTS frames were found in these bytes.' });
    notes.push({ level: 'warn', text: first < end ? `Found ${fmtBytes(first - pos)} of unknown data before the first ADTS frame.` : 'No valid ADTS frames were found.' });
  }

  const key = first < end ? streamKey(b, first) : 0;
  const sfi = first < end ? sfiOf(b, first) : 4;
  const sr = SAMPLE_RATES[sfi] ?? 44100;
  const items: Segment[] = [];
  const frames: AdtsFrame[] = [];
  let samples = 0, audioBytes = 0, headerBytes = 0, badBytes = 0;
  let allVbr = true;
  let p = first;
  while (p < end) {
    if (validAt(b, p, end) && streamKey(b, p) === key) {
      const len = frameLenOf(b, p);
      const q = p + len;
      if (q >= end || validAt(b, q, end) && streamKey(b, q) === key || q + 7 > end) {
        const fe = Math.min(q, end);
        const t0 = samples / sr;
        samples += blocksOf(b, p) * SAMPLES_PER_BLOCK;
        const f = new AdtsFrame(b, p, fe, [t0, samples / sr], frames.length);
        if (q > end) f.warning = `This frame should be ${len} bytes, but only ${end - p} remain. The file is cut short.`;
        frames.push(f);
        items.push(f);
        audioBytes += fe - p;
        headerBytes += headerLen(b, p);
        if (fullnessOf(b, p) !== 0x7ff) allVbr = false;
        p = fe;
        continue;
      }
    }
    const q = findSync(b, p + 1, end, key, 2);
    items.push({ kind: 'unknown', type: 'aac.garbage', name: 'Unparsed bytes', start: p, end: q, summary: fmtBytes(q - p), warning: 'These bytes are not valid ADTS frames. A player skips them and searches for the next sync word.' });
    badBytes += q - p;
    p = q;
  }

  let gj = items.length - 1;
  while (gj >= 0 && !(items[gj] instanceof AdtsFrame)) gj--;
  const gi = items.findIndex((s) => s instanceof AdtsFrame);
  const dur = samples / sr;
  if (gi < 0) top.push(...items);
  else {
    top.push(...items.slice(0, gi));
    const inner = items.slice(gi, gj + 1);
    top.push({ kind: 'audio', type: 'aac.frames', name: 'Audio frames', start: inner[0].start, end: inner[inner.length - 1].end, children: inner, time: [0, dur], summary: `${plural(frames.length, 'frame')} · ${fmtSec(dur)}` });
    top.push(...items.slice(gj + 1));
  }
  top.push(...tail.segments);

  // Encoder name: ffmpeg writes "Lavc…" in a fill element of the first frame.
  let encoder: string | undefined;
  if (frames.length) {
    const f0 = frames[0];
    for (let i = f0.start; i + 4 < f0.end; i++) {
      if (b[i] === 0x4c && (matches(b, i, 'Lavc') || matches(b, i, 'Lavf'))) {
        let j = i;
        while (j < f0.end && b[j] >= 0x20 && b[j] < 0x7f) j++;
        encoder = String.fromCharCode(...b.subarray(i, j));
        break;
      }
    }
  }
  if (!encoder) encoder = tags.find((t) => t.rawKey === 'TSSE' || t.rawKey === 'TENC')?.value;

  const prof = first < end ? profileOf(b, first) : 1;
  const ch = first < end ? chanOf(b, first) : 2;
  const channels = ch === 7 ? 8 : ch || undefined;
  const bitrate = dur > 0 ? Math.round((audioBytes * 8) / dur) : undefined;
  if (frames.length) {
    notes.push({ level: 'info', text: `Each ADTS frame repeats its header, so a player can start anywhere, as in a radio stream. The headers take ${fmtBytes(headerBytes)} (${((headerBytes / Math.max(1, audioBytes)) * 100).toFixed(1)}% of the audio).` });
    notes.push({ level: 'info', text: 'AAC encoders add silent "priming" samples at the start (usually 1,024 or 2,112). ADTS has no field to record this, so players cannot trim it exactly. MP4 files can.' });
    if (allVbr) notes.push({ level: 'info', text: `Every frame has buffer fullness 0x7FF, which marks variable bitrate. The average is ${fmtKbps(bitrate ?? 0)}.` });
    notes.push({ level: 'info', text: 'ADTS has no duration field or seek table. To know the exact length, a player must read every frame; many just estimate from the file size.' });
  }
  if (badBytes) notes.push({ level: 'warn', text: `Found ${fmtBytes(badBytes)} of damaged or unknown data between frames.` });
  if (tagBytes) notes.push({ level: 'info', text: `Tags take ${fmtBytes(tagBytes)} (${((tagBytes / size) * 100).toFixed(1)}% of the file).` });

  const mpegId = first < end ? (b[first + 1] >> 3) & 1 : 0;
  return {
    format: 'aac', container: 'ADTS', size, segments: top, frames, tags, pictures, notes,
    audio: {
      codec: CODECS[prof], codecLong: `${mpegId ? 'MPEG-2' : 'MPEG-4'} AAC ${PROFILES[prof]} in ADTS`,
      sampleRate: sr, channels, channelMode: ch ? channelName(channels!) : 'Set in stream',
      duration: dur, totalSamples: samples, bitrate, bitrateMode: frames.length ? (allVbr ? 'VBR' : 'CBR') : undefined,
      lossless: false, encoder,
    },
  };
}

const adtsExplanations: Record<string, Explanation> = {
  'aac.frames': {
    title: 'Audio frames',
    body: 'The AAC audio as a run of ADTS frames. Each frame holds 1,024 samples (about 23 ms at 44.1 kHz). There is no index, so a player finds frames one by one.',
  },
  'aac.frame': {
    title: 'ADTS frame',
    body: 'One AAC frame with a small header in front. The header repeats the sample rate and channel count every time, so a decoder can join a stream at any point, like a radio.',
  },
  'aac.header': {
    title: 'ADTS header',
    body: 'Seven packed bytes (nine with a CRC). They start with the sync word `0xFFF` and give the AAC profile, sample rate, channels and the frame length. The two layer bits are always 0, which tells ADTS apart from MP3.',
  },
  'aac.rawdata': {
    title: 'Raw data block',
    body: 'The compressed AAC audio: channel elements with scale factors and Huffman-coded spectral values. ffmpeg also puts a fill element with its version name in the first frame.',
  },
  'aac.junk': {
    title: 'Data before the first frame',
    body: 'Bytes before the first ADTS frame that no tag claims. Players skip them by searching for the sync word.',
  },
  'aac.garbage': {
    title: 'Unparsed bytes',
    body: 'Bytes between frames that are not valid ADTS frames. The file may be damaged. A player searches for the next sync word and continues.',
  },
};

export const adtsParser: FormatParser = {
  id: 'aac',
  detect,
  parse,
  explanations: { ...adtsExplanations, ...trailingTagExplanations, ...id3Explanations },
};
